import { z } from 'zod';
import {
  createModel,
  currencySchema,
  partySchema,
  productPurchaseSchema,
  QueryMany,
  QueryOne,
  refSchema,
} from '../../common';
import { dateSchema, quantitySchema } from '../../validation';
import { subscriptionStatusSchema } from './subscription.status.model';
import { subscriptionAddonSchema } from './subscription.addon.model';
import { subscriptionPaymentMethodSchema } from './subscription.payment-method.model';
import { subscriptionTrialSchema } from './subscription.trial.model';
import {
  subscriptionAddonOverrideSchema,
  subscriptionPricingOverrideSchema,
} from './subscription.pricing-override.model';
import {
  subscriptionBalanceSchema,
  subscriptionModelSchema,
} from './subscription.usage.model';

/**
 * `Subscription` — one party's grant of the right to use the platform.
 *
 * One active grant per `(owner, purchase.product, purchase.unit)`. An Org
 * can hold Hero vehicle seats and Hero order credits (and a Wathiqa grant)
 * as separate documents. Created lazily; absent for anyone who has never
 * used the product (FeaturesService treats "missing subscription" as the
 * implicit free plan for that purchase key).
 *
 * Two models share this document — see {@link subscriptionModelSchema}. A
 * `period` subscription expires on a date and renews; a `usage` one
 * expires at a count and is topped up. `model` is derived from
 * `purchase.unit` at write (`vehicle` → period, `order` → usage).
 *
 * See `apps/backend/src/app/sales/subscription/README.md` for the full lifecycle.
 */
export const SubscriptionSchema = createModel({
  /**
   * Who holds the grant — full Party snapshot, same shape as
   * `Invoice.payer`.
   *
   * `target: 'org'` for a company, `target: 'driver'` for a freelancer
   * owner-operator.
   */
  owner: partySchema,
  /** Human-readable ref (SUB-2026-000123). */
  ref: refSchema,

  /** Lifecycle. */
  status: subscriptionStatusSchema,

  /**
   * Currency — FROZEN at subscribe. Renewals + addons + refunds all use this.
   * Mid-contract currency change is forbidden; cancel + re-subscribe to move.
   */
  currency: currencySchema,

  /**
   * What was sold — business, product (audience offering), plan, unit.
   * FROZEN commercial facts; see `productPurchaseSchema`.
   */
  purchase: productPurchaseSchema,

  /**
   * Whether this grant expires on a date or at a count — see
   * {@link subscriptionModelSchema}. Derived from `purchase.unit` at
   * write; stored so readers do not re-derive.
   */
  model: subscriptionModelSchema.optional(),

  // ── `period` model ────────────────────────────────────────────────

  interval: z.enum(['month', 'semester', 'year']).optional(),
  /**
   * Committed vehicle quantity (the seat baseline). Overage charged when
   * fleet > committed. Only on `purchase.unit === 'vehicle'`.
   */
  committedVehicleQty: quantitySchema,

  // ── `usage` model ─────────────────────────────────────────────────

  /**
   * The credit bucket. Required in practice on `usage`.
   */
  balance: subscriptionBalanceSchema.optional(),

  /** Live addons attached to the subscription. */
  addons: z.array(subscriptionAddonSchema).default([]),

  /** Per-owner pricing override (enterprise deals). All amounts in `Subscription.currency`. */
  pricingOverride: subscriptionPricingOverrideSchema.optional(),
  /** Per-addon overrides keyed by addon code. */
  addonOverrides: z.record(z.string(), subscriptionAddonOverrideSchema).optional(),

  currentPeriodStart: dateSchema.optional(),
  currentPeriodEnd: dateSchema.optional(),
  trialEndsAt: dateSchema.optional(),
  /** What the trial grants before the first payment — see {@link subscriptionTrialSchema}. */
  trial: subscriptionTrialSchema.optional(),
  /** User-requested cancel that takes effect at `currentPeriodEnd`. */
  cancelAtPeriodEnd: z.boolean().default(false),

  /** Saved Moyasar token (never raw card data). */
  paymentMethod: subscriptionPaymentMethodSchema.optional(),

  failedAttempts: z.coerce.number().int().min(0).default(0),
  pastDueSince: dateSchema.optional(),
});

export type Subscription = z.infer<typeof SubscriptionSchema.model>;
export type SubscriptionData = z.infer<typeof SubscriptionSchema.data>;
export type SubscriptionPartial = z.infer<typeof SubscriptionSchema.partial>;
export type SubscriptionQueryMany = QueryMany<Subscription>;
export type SubscriptionQueryOne = QueryOne<Subscription>;
